import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { getFoundationRefRows } from "@/lib/api";

// Searchable dropdown over a synced Foundation reference list. Values can
// only come from the list — never free-typed.
export default function FoundationRefPicker({ listId, value, onChange, placeholder = "Select…", disabled, allowClear = true, testId = "ref-picker", className = "" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [synced, setSynced] = useState(true);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      getFoundationRefRows(listId, q).then(r => { setRows(r.data.rows || []); setSynced(r.data.synced_once); }).catch(() => setRows([]));
    }, 150);
    return () => clearTimeout(t);
  }, [open, q, listId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled}
                className={`h-8 px-2 text-xs border border-slate-200 rounded-md bg-white inline-flex items-center gap-1.5 min-w-[160px] justify-between disabled:opacity-50 ${className}`}
                data-testid={testId}>
          <span className={`truncate font-mono ${value ? "text-slate-900" : "text-slate-400"}`}>{value || placeholder}</span>
          <span className="flex items-center gap-1 shrink-0">
            {allowClear && value && !disabled && (
              <X className="w-3 h-3 text-slate-400 hover:text-rose-600" onClick={(e) => { e.stopPropagation(); onChange(null); }} data-testid={`${testId}-clear`} />
            )}
            <ChevronsUpDown className="w-3 h-3 text-slate-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search code or name…" value={q} onValueChange={setQ} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>{synced ? "No match in the synced Foundation list." : "Awaiting Foundation access — list not synced yet."}</CommandEmpty>
            <CommandGroup>
              {rows.map(r => (
                <CommandItem key={r._pk} value={r._pk} onSelect={() => { onChange(r._pk, r); setOpen(false); }}
                             className="text-xs gap-2" data-testid={`${testId}-opt-${r._pk}`}>
                  <Check className={`w-3 h-3 ${value === r._pk ? "opacity-100" : "opacity-0"}`} />
                  <code className="w-24 shrink-0">{r._pk}</code>
                  <span className="truncate">{r.label}</span>
                  {r.email_address && <span className="text-slate-400 truncate">{r.email_address}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
